import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import axios from 'axios';
import { getUserPermissions, isAuthorized, getUserTenants } from '../utils/Permissions';
import '../styles/main.css';

const PERMISSION_KEYS = [
    { key: 'EA', label: 'Evaluation Accounts' },
    { key: 'PA1', label: 'Paid Accounts (1 connection)' },
    { key: 'PA', label: 'Paid Accounts (unlimited)' },
    { key: 'LA', label: 'Live Accounts' },
];

export const Admin = () => {
    const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();

    // Permissions loading state
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    // Onboard state
    const [email, setEmail] = useState('');
    const [permissionsKey, setPermissionsKey] = useState('');
    const [selectedTenants, setSelectedTenants] = useState([]);
    const [isTrial, setIsTrial] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const adminTenants = getUserTenants();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResult, setSearchResult] = useState(null);
    const [searchError, setSearchError] = useState(null);

    // Update permissions state
    const [updatePermissionsKey, setUpdatePermissionsKey] = useState('');
    const [updateTenants, setUpdateTenants] = useState([]);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateResult, setUpdateResult] = useState(null);
    const [updateError, setUpdateError] = useState(null);

    // Offboard state
    const [offboardEmail, setOffboardEmail] = useState('');
    const [offboardLoading, setOffboardLoading] = useState(false);
    const [offboardResult, setOffboardResult] = useState(null);
    const [offboardError, setOffboardError] = useState(null);
    const [offboardConfirm, setOffboardConfirm] = useState(null);

    useEffect(() => {
        const loadPermissions = async () => {
            if (isAuthenticated && user?.sub) {
                const token = await getAccessTokenSilently();
                const userId = user.sub.split('|')[1];
                await getUserPermissions(token, userId);
                setPermissionsLoaded(true);
            }
        };
        loadPermissions();
    }, [isAuthenticated, user]);

    if (!permissionsLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!isAuthorized('admin')) {
        return <Navigate to="/connect" replace />;
    }

    const getAuthHeaders = async () => {
        const token = await getAccessTokenSilently();
        const userId = user.sub.split('|')[1];
        return {
            headers: { Authorization: `Bearer ${token}` },
            params: { uid: userId },
        };
    };

    const handleOnboard = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResult(null);
        setError(null);

        try {
            const config = await getAuthHeaders();
            const response = await axios.post(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/admin/user/onboard`,
                { email, permissionsKey, tenants: selectedTenants, isTrial },
                config
            );
            setResult(response.data);
            setEmail('');
            setPermissionsKey('');
            setSelectedTenants([]);
            setIsTrial(false);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setSearchLoading(true);
        setSearchResult(null);
        setSearchError(null);
        setUpdateResult(null);
        setUpdateError(null);

        try {
            const config = await getAuthHeaders();
            const response = await axios.get(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/admin/user/search`,
                { ...config, params: { ...config.params, query: searchQuery } }
            );
            setSearchResult(response.data);
            setUpdateTenants(response.data.tenants || []);
        } catch (err) {
            setSearchError(err.response?.data?.error || err.message);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleUpdatePermissions = async (e) => {
        e.preventDefault();
        if (!searchResult) return;
        setUpdateLoading(true);
        setUpdateResult(null);
        setUpdateError(null);

        try {
            const config = await getAuthHeaders();
            const response = await axios.put(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/admin/user/permissions`,
                { userId: searchResult.userId, permissionsKey: updatePermissionsKey, tenants: updateTenants },
                config
            );
            setUpdateResult(response.data);
            // Refresh user data
            setSearchResult((prev) => ({
                ...prev,
                permissions: response.data.permissions,
                tenants: response.data.tenants,
            }));
        } catch (err) {
            setUpdateError(err.response?.data?.error || err.message);
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleOffboardSearch = async (e) => {
        e.preventDefault();
        setOffboardLoading(true);
        setOffboardResult(null);
        setOffboardError(null);
        setOffboardConfirm(null);

        try {
            const config = await getAuthHeaders();
            const response = await axios.get(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/admin/user/search`,
                { ...config, params: { ...config.params, query: offboardEmail } }
            );
            setOffboardConfirm(response.data);
        } catch (err) {
            setOffboardError(err.response?.data?.error || err.message);
        } finally {
            setOffboardLoading(false);
        }
    };

    const handleOffboardConfirm = async () => {
        if (!offboardConfirm) return;
        setOffboardLoading(true);
        setOffboardError(null);

        try {
            const config = await getAuthHeaders();
            const response = await axios.post(
                `${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/admin/user/offboard`,
                { userId: offboardConfirm.userId },
                config
            );
            setOffboardResult(response.data);
            setOffboardConfirm(null);
            setOffboardEmail('');
        } catch (err) {
            setOffboardError(err.response?.data?.error || err.message);
        } finally {
            setOffboardLoading(false);
        }
    };

    const getConnectionName = (buid, tradeConfig) => {
        const config = tradeConfig[buid];
        if (config?.connectionName) return config.connectionName;
        if (config?.name) return config.name;
        // Check if any account group has a name hint
        if (config?.accountGroups?.length > 0) {
            const firstGroup = config.accountGroups[0];
            if (firstGroup?.accountsToTrade?.length > 0) {
                return firstGroup.accountsToTrade[0].name || null;
            }
        }
        return null;
    };

    const renderTradeConfig = (tradeConfig) => {
        if (!tradeConfig) return <p className="text-muted fst-italic">No trade config found</p>;

        const connections = Object.keys(tradeConfig).filter((k) => k !== 'masterTradeSettings');
        return (
            <>
                {tradeConfig.masterTradeSettings && (
                    <div className="mb-3 p-2 rounded" style={{ background: '#f8f9fa' }}>
                        <span className="me-3">
                            <strong>Master Trading:</strong>{' '}
                            <Badge bg={tradeConfig.masterTradeSettings.isMasterTradingEnabled ? 'success' : 'secondary'}>
                                {tradeConfig.masterTradeSettings.isMasterTradingEnabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </span>
                        <span>
                            <strong>Auto-Enable:</strong>{' '}
                            <Badge bg={tradeConfig.masterTradeSettings.isMasterAutomaticallyEnabled ? 'success' : 'secondary'}>
                                {tradeConfig.masterTradeSettings.isMasterAutomaticallyEnabled ? 'Yes' : 'No'}
                            </Badge>
                        </span>
                    </div>
                )}
                {connections.length === 0 && (
                    <p className="text-muted fst-italic">No connections configured</p>
                )}
                {connections.map((buid) => {
                    const config = tradeConfig[buid];
                    if (!config?.accountGroups) return null;
                    const connectionName = getConnectionName(buid, tradeConfig);
                    return (
                        <Card key={buid} className="admin-connection-card">
                            <Card.Body>
                                <div className="admin-connection-header">
                                    {connectionName && (
                                        <span>{connectionName}</span>
                                    )}
                                    <span className="text-muted ms-2">({buid})</span>
                                </div>
                                {config.accountGroups.map((group, idx) => (
                                    <div key={idx} className="admin-group-block">
                                        <strong>{group.groupName || `Group ${idx + 1}`}</strong>
                                        <Table size="sm" className="mt-1">
                                            <tbody>
                                                <tr>
                                                    <td>Trading</td>
                                                    <td>
                                                        <Badge bg={group.isTradingEnabled ? 'success' : 'secondary'} className="badge-sm">
                                                            {group.isTradingEnabled ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                <tr><td>Risk Size</td><td>{group.riskSize}</td></tr>
                                                <tr><td>Daily Loss Limit</td><td>{group.dailyLossLimit}</td></tr>
                                                <tr><td>Daily Profit Limit</td><td>{group.dailyProfitLimit}</td></tr>
                                                <tr><td>Contract Limit</td><td>{group.contractLimit}</td></tr>
                                                <tr>
                                                    <td>Stack Trades</td>
                                                    <td>
                                                        <Badge bg={group.stackTrades ? 'info' : 'secondary'} className="badge-sm">
                                                            {group.stackTrades ? 'Yes' : 'No'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Single Direction</td>
                                                    <td>
                                                        <Badge bg={group.singleDirectionTrading ? 'info' : 'secondary'} className="badge-sm">
                                                            {group.singleDirectionTrading ? 'Yes' : 'No'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Trailing Stop</td>
                                                    <td>
                                                        <Badge bg={group.useTrailingStop ? 'info' : 'secondary'} className="badge-sm">
                                                            {group.useTrailingStop ? 'Yes' : 'No'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Algos</td>
                                                    <td>
                                                        {group.savedAlgos?.length > 0
                                                            ? group.savedAlgos.map((a) => (
                                                                <Badge key={a} bg="primary" className="me-1">{a}</Badge>
                                                            ))
                                                            : <span className="text-muted">None</span>
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Accounts</td>
                                                    <td>
                                                        {group.accountsToTrade?.length > 0
                                                            ? group.accountsToTrade.map((a) => (
                                                                <Badge
                                                                    key={a.name}
                                                                    bg={a.enableTrading ? 'success' : 'secondary'}
                                                                    className="me-1"
                                                                >
                                                                    {a.name}
                                                                </Badge>
                                                            ))
                                                            : <span className="text-muted">None</span>
                                                        }
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    );
                })}
            </>
        );
    };

    return (
        <Container className="admin-page">
            <h2>Admin</h2>

            {/* Search User */}
            <Card className="admin-card">
                <Card.Body>
                    <Card.Title>Search User</Card.Title>
                    <Form onSubmit={handleSearch}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">User ID or Email</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    placeholder="Enter userId or email"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    required
                                />
                                <Button type="submit" variant="primary" disabled={searchLoading} style={{ minWidth: '100px' }}>
                                    {searchLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : 'Search'}
                                </Button>
                            </div>
                        </Form.Group>
                    </Form>
                    {searchError && (
                        <Alert variant="danger" className="mt-3 mb-0">{searchError}</Alert>
                    )}
                    {searchResult && (
                        <div className="mt-3">
                            <h5 className="admin-section-title">User Details</h5>
                            <Table bordered size="sm" className="admin-user-details">
                                <tbody>
                                    <tr><td>User ID</td><td>{searchResult.userId}</td></tr>
                                    <tr><td>Email</td><td>{searchResult.username}</td></tr>
                                    <tr><td>Server</td><td>{searchResult.serverName || <span className="text-muted">None</span>}</td></tr>
                                    <tr><td>Stripe ID</td><td>{searchResult.stripeUserId || <span className="text-muted">None</span>}</td></tr>
                                    <tr>
                                        <td>Permissions</td>
                                        <td>
                                            {searchResult.permissions?.permissions?.length > 0
                                                ? searchResult.permissions.permissions.map((p) => (
                                                    <Badge key={p} bg="info" className="me-1">{p}</Badge>
                                                ))
                                                : <span className="text-muted">None</span>
                                            }
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Algorithms</td>
                                        <td>
                                            {searchResult.permissions?.algorithms?.length > 0
                                                ? searchResult.permissions.algorithms.map((a) => (
                                                    <Badge key={a} bg="dark" className="me-1">{a}</Badge>
                                                ))
                                                : <span className="text-muted">None</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            {/* Update Permissions */}
                            <h5 className="admin-section-title">Update Permissions</h5>
                            <div className="admin-update-form">
                                <Form onSubmit={handleUpdatePermissions}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <Form.Select
                                            value={updatePermissionsKey}
                                            onChange={(e) => setUpdatePermissionsKey(e.target.value)}
                                            required
                                            style={{ maxWidth: '280px' }}
                                        >
                                            <option value="">Select access level</option>
                                            {PERMISSION_KEYS.map((p) => (
                                                <option key={p.key} value={p.key}>{p.label}</option>
                                            ))}
                                        </Form.Select>
                                        <Button type="submit" variant="warning" disabled={updateLoading || updateTenants.length === 0} style={{ minWidth: '90px' }}>
                                            {updateLoading ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : 'Update'}
                                        </Button>
                                    </div>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="fw-medium mb-1">Tenant(s)</Form.Label>
                                        {adminTenants.map((tenant) => (
                                            <Form.Check
                                                key={tenant}
                                                type="checkbox"
                                                label={tenant}
                                                checked={updateTenants.includes(tenant)}
                                                onChange={(e) => {
                                                    setUpdateTenants((prev) =>
                                                        e.target.checked
                                                            ? [...prev, tenant]
                                                            : prev.filter((t) => t !== tenant)
                                                    );
                                                }}
                                                style={{ textTransform: 'capitalize' }}
                                            />
                                        ))}
                                    </Form.Group>
                                </Form>
                            </div>
                            {updateResult && (
                                <Alert variant="success" className="mt-2 mb-0">
                                    Permissions updated successfully
                                </Alert>
                            )}
                            {updateError && (
                                <Alert variant="danger" className="mt-2 mb-0">{updateError}</Alert>
                            )}

                            {/* Trade Config */}
                            <h5 className="admin-section-title">Trade Config</h5>
                            {renderTradeConfig(searchResult.tradeConfig)}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Onboard User */}
            <Card className="admin-card">
                <Card.Body>
                    <Card.Title>Onboard User</Card.Title>
                    <Form onSubmit={handleOnboard}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="user@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">Access Level</Form.Label>
                            <Form.Select
                                value={permissionsKey}
                                onChange={(e) => setPermissionsKey(e.target.value)}
                                required
                            >
                                <option value="">Select access level</option>
                                {PERMISSION_KEYS.map((p) => (
                                    <option key={p.key} value={p.key}>{p.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">Tenant(s)</Form.Label>
                            {adminTenants.map((tenant) => (
                                <Form.Check
                                    key={tenant}
                                    type="checkbox"
                                    label={tenant}
                                    checked={selectedTenants.includes(tenant)}
                                    onChange={(e) => {
                                        setSelectedTenants((prev) =>
                                            e.target.checked
                                                ? [...prev, tenant]
                                                : prev.filter((t) => t !== tenant)
                                        );
                                    }}
                                    style={{ textTransform: 'capitalize' }}
                                />
                            ))}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Trial User"
                                checked={isTrial}
                                onChange={(e) => setIsTrial(e.target.checked)}
                            />
                        </Form.Group>
                        <Button type="submit" variant="primary" disabled={loading || selectedTenants.length === 0} style={{ minWidth: '140px' }}>
                            {loading ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Onboarding...</>
                            ) : 'Onboard User'}
                        </Button>
                    </Form>
                    {result && (
                        <Alert variant="success" className="mt-3 mb-0">
                            User onboarded: <strong>{result.email}</strong> (userId: {result.userId}, server: {result.serverName})
                        </Alert>
                    )}
                    {error && (
                        <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Offboard User */}
            <Card className="admin-card">
                <Card.Body>
                    <Card.Title>Offboard User</Card.Title>
                    <Form onSubmit={handleOffboardSearch}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">Email</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="email"
                                    placeholder="user@example.com"
                                    value={offboardEmail}
                                    onChange={(e) => { setOffboardEmail(e.target.value); setOffboardConfirm(null); setOffboardResult(null); setOffboardError(null); }}
                                    required
                                />
                                <Button type="submit" variant="danger" disabled={offboardLoading} style={{ minWidth: '100px' }}>
                                    {offboardLoading && !offboardConfirm ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : 'Search'}
                                </Button>
                            </div>
                        </Form.Group>
                    </Form>
                    {offboardError && (
                        <Alert variant="danger" className="mt-3 mb-0">{offboardError}</Alert>
                    )}
                    {offboardConfirm && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            <p className="mb-2">
                                Are you sure you want to offboard <strong>{offboardConfirm.username}</strong> (userId: {offboardConfirm.userId})?
                            </p>
                            <div className="d-flex gap-2">
                                <Button variant="danger" size="sm" onClick={handleOffboardConfirm} disabled={offboardLoading}>
                                    {offboardLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : 'Confirm Offboard'}
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => setOffboardConfirm(null)}>
                                    Cancel
                                </Button>
                            </div>
                        </Alert>
                    )}
                    {offboardResult && (
                        <Alert variant="success" className="mt-3 mb-0">
                            User <strong>{offboardResult.userId}</strong> has been offboarded.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};
